// react and js

// mui

// components

// hooks

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
// Single source of truth for the supported UI languages.
//   - code:       BCP-47 code used in URLs and i18next
//   - nativeName: shown in the language selector
//   - dir:        "ltr" or "rtl" — drives MUI theme direction
export const LANGUAGES = [
  { code: "en", nativeName: "English", dir: "ltr" },
  { code: "de", nativeName: "Deutsch", dir: "ltr" },
  { code: "fr", nativeName: "Français", dir: "ltr" },
  { code: "es", nativeName: "Español", dir: "ltr" },
  { code: "zh-CN", nativeName: "简体中文", dir: "ltr" },
  { code: "zh-TW", nativeName: "繁體中文", dir: "ltr" },
  { code: "ja", nativeName: "日本語", dir: "ltr" },
  { code: "pt-BR", nativeName: "Português", dir: "ltr" },
  { code: "ar", nativeName: "العربية", dir: "ltr" },
  { code: "ko", nativeName: "한국어", dir: "ltr" },
  { code: "pl", nativeName: "Polski", dir: "ltr" },
];

export const DEFAULT_LANGUAGE = "en";

export const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);

// Non-default codes are the ones that appear as URL prefixes (English has none).
export const NON_DEFAULT_CODES = SUPPORTED_CODES.filter(
  (c) => c !== DEFAULT_LANGUAGE
);

export function getLanguageMeta(code) {
  return LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
}

// Reads the first path segment and returns { code, basename, strippedPath }.
// If the first segment is a non-default supported code, that language is active
// and basename is "/<code>"; otherwise English with basename "/".
export function detectLanguageFromPath(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (first && NON_DEFAULT_CODES.includes(first)) {
    return {
      code: first,
      basename: `/${first}`,
      strippedPath: `/${segments.slice(1).join("/")}`,
    };
  }
  return {
    code: DEFAULT_LANGUAGE,
    basename: "/",
    strippedPath: pathname || "/",
  };
}

// Build a new full URL when switching the language from the current location.
// English drops the prefix; other languages get "/<code>" prepended.
export function buildLanguageSwitchUrl(targetCode, currentPathname, currentSearch, currentHash) {
  const { strippedPath } = detectLanguageFromPath(currentPathname);
  const prefix = targetCode === DEFAULT_LANGUAGE ? "" : `/${targetCode}`;
  const path = strippedPath === "/" ? "" : strippedPath;
  return `${prefix}${path}${currentSearch || ""}${currentHash || ""}` || "/";
}

// Localize an absolute URL pointing at the main website by inserting "/<code>"
// right after the origin (between protocol+host and path). English (default)
// returns the URL unchanged. Falsy/invalid inputs are returned as-is so call
// sites don't need extra guards.
export function localizeExternalUrl(url, languageCode) {
  if (!url || languageCode === DEFAULT_LANGUAGE) return url;
  if (!SUPPORTED_CODES.includes(languageCode)) return url;
  try {
    const u = new URL(url);
    // Avoid double-prefixing if the URL already carries a known locale prefix.
    const firstSegment = u.pathname.split("/").filter(Boolean)[0];
    if (firstSegment && SUPPORTED_CODES.includes(firstSegment)) {
      return url;
    }
    const restOfPath = u.pathname === "/" ? "" : u.pathname;
    return `${u.origin}/${languageCode}${restOfPath}${u.search}${u.hash}`;
  } catch (e) {
    return url;
  }
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
